import { useEffect, useRef, useState, type ReactNode } from 'react'
import { useMutation } from '@tanstack/react-query'
import { Loader2, Plus, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { cn } from '@/lib/utils'
import {
  resourceCenterApi,
  type SupportActivityType,
  type UnavailabilityType,
} from '@/api/resourceCenterApi'
import {
  useInvalidateResourceCenter,
  useSupportActivityTypes,
  useUnavailabilityTypes,
} from '@/hooks/useResourceCenter'
import { ADD_ROW, DELETE_BUTTON, FORM_INPUT, HEADER_CELL, QUIET_INPUT, ROW } from './listStyles'

export type CenterListTab = 'support' | 'unavailability'

/**
 * Center Setup → the SA and UA pick lists (z8nrz7f0vu). Predefined so hours
 * are reported consistently: a profile picks "EQM", it does not type
 * "equipment maint." into a box.
 */
export function CenterListsDialog({
  open,
  onOpenChange,
  canManage,
  tab,
  onTabChange,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  canManage: boolean
  tab: CenterListTab
  onTabChange: (tab: CenterListTab) => void
}) {
  const [error, setError] = useState<string | null>(null)
  return (
    <Dialog
      open={open}
      onOpenChange={o => {
        setError(null)
        onOpenChange(o)
      }}
    >
      <DialogContent
        className='max-h-[90vh] overflow-y-auto sm:max-w-3xl'
        onOpenAutoFocus={e => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>Center lists</DialogTitle>
          <DialogDescription>
            What a resource profile picks from, so everyone reports hours the same way.
          </DialogDescription>
        </DialogHeader>
        {error && (
          <p className='rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive'>{error}</p>
        )}
        <Tabs
          value={tab}
          onValueChange={v => {
            setError(null)
            onTabChange(v as CenterListTab)
          }}
        >
          <TabsList>
            <TabsTrigger value='support'>Support activities</TabsTrigger>
            <TabsTrigger value='unavailability'>Unavailability</TabsTrigger>
          </TabsList>
          <TabsContent value='support' className='pt-3'>
            <SupportActivityList canManage={canManage} onError={setError} />
          </TabsContent>
          <TabsContent value='unavailability' className='pt-3'>
            <UnavailabilityList canManage={canManage} onError={setError} />
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  )
}

type OnError = (message: string | null) => void
const messageOf = (e: unknown) => (e instanceof Error ? e.message : 'Something went wrong.')

/** Hint, header row, hairline-separated rows and the add row — shared by both lists. */
function ListFrame({
  hint,
  cols,
  headers,
  loading,
  empty,
  children,
  addRow,
}: {
  hint: string
  cols: string
  headers: string[]
  loading: boolean
  empty: boolean
  children: ReactNode
  addRow: ReactNode
}) {
  return (
    <div>
      <p className='mb-3 text-sm text-muted-foreground'>{hint}</p>
      <div className={cn(cols, 'pb-1.5')}>
        {headers.map((h, i) => (
          <span key={i} className={HEADER_CELL}>
            {h}
          </span>
        ))}
      </div>
      <div className='divide-y divide-border/60 border-y border-border/60'>
        {loading && (
          <div className='py-6 text-center'>
            <Loader2 className='mx-auto h-4 w-4 animate-spin text-muted-foreground' />
          </div>
        )}
        {children}
        {empty && <p className='py-6 text-center text-sm text-muted-foreground'>Nothing here yet. Add the first one below.</p>}
      </div>
      {addRow}
    </div>
  )
}

// ── Support activities ───────────────────────────────────────────────────────

const SA_COLS = 'grid grid-cols-[5.5rem_minmax(0,1fr)_6.5rem_3.25rem_7rem] items-center gap-1.5'

/**
 * A row's edit buffer. A refetch that changes the row is adopted only while the
 * buffer still holds the last copy it took, so unsaved edits survive. Call
 * `markSaved` with the draft that was SUBMITTED, so the refetched
 * (server-normalised) row replaces it — but not an edit made while the save
 * was in flight.
 */
function useRowDraft<T>(server: T) {
  const key = JSON.stringify(server)
  const [draft, setDraft] = useState(server)
  const synced = useRef(key)
  useEffect(() => {
    const prev = synced.current
    if (prev === key) return
    synced.current = key
    setDraft(d => (JSON.stringify(d) === prev ? (JSON.parse(key) as T) : d))
  }, [key])
  const markSaved = (submitted: T) => {
    synced.current = JSON.stringify(submitted)
  }
  const undo = () => setDraft(JSON.parse(key) as T)
  return { draft, setDraft, dirty: JSON.stringify(draft) !== key, markSaved, undo }
}

function SupportActivityList({ canManage, onError }: { canManage: boolean; onError: OnError }) {
  const rows = useSupportActivityTypes()
  return (
    <ListFrame
      hint='Reserved first each working day; whatever is left goes to Process and Review. The default fills in when someone adds the activity to a profile.'
      cols={SA_COLS}
      headers={['Code', 'Activity', 'Default', 'Active', '']}
      loading={rows.isLoading}
      empty={rows.data?.length === 0}
      addRow={canManage && <SupportActivityRow key='new' canManage onError={onError} />}
    >
      {rows.data?.map(r => (
        <SupportActivityRow key={r.id} row={r} canManage={canManage} onError={onError} />
      ))}
    </ListFrame>
  )
}

function SupportActivityRow({
  row,
  canManage,
  onError,
}: {
  row?: SupportActivityType
  canManage: boolean
  onError: OnError
}) {
  const invalidate = useInvalidateResourceCenter()
  const { draft, setDraft, dirty, markSaved, undo } = useRowDraft({
    code: row?.code ?? '',
    name: row?.name ?? '',
    hours: row ? String(row.defaultHoursPerDay) : '',
  })

  const isNew = !row
  const hours = Number(draft.hours)
  const valid = !!draft.code.trim() && !!draft.name.trim() && draft.hours !== '' && hours >= 0 && hours <= 24
  const done = () => {
    onError(null)
    void invalidate()
  }
  const fail = (e: unknown) => onError(messageOf(e))

  const save = useMutation({
    // The submitted draft travels as the variables: edits made while the save
    // is in flight are compared against it, not against whatever is typed now.
    mutationFn: (sent: typeof draft) => {
      const body = { code: sent.code.trim(), name: sent.name.trim(), defaultHoursPerDay: Number(sent.hours) }
      return row
        ? resourceCenterApi.updateSupportActivityType(row.id, body)
        : resourceCenterApi.createSupportActivityType(body)
    },
    onSuccess: (_, sent) => {
      if (isNew) setDraft(d => (JSON.stringify(d) === JSON.stringify(sent) ? { code: '', name: '', hours: '' } : d))
      else markSaved(sent)
      done()
    },
    onError: fail,
  })
  const toggle = useMutation({
    mutationFn: (active: boolean) => resourceCenterApi.updateSupportActivityType(row!.id, { active }),
    onSuccess: done,
    onError: fail,
  })
  const remove = useMutation({
    mutationFn: () => resourceCenterApi.removeSupportActivityType(row!.id),
    onSuccess: done,
    onError: fail,
  })

  const input = isNew ? FORM_INPUT : QUIET_INPUT
  return (
    <div className={isNew ? ADD_ROW : undefined}>
      <div className={cn(SA_COLS, !isNew && ROW, row && !row.active && 'text-muted-foreground')}>
        <Input
          value={draft.code}
          onChange={e => setDraft(d => ({ ...d, code: e.target.value.toUpperCase() }))}
          placeholder={isNew ? 'Code' : ''}
          aria-label='Code'
          maxLength={12}
          readOnly={!canManage}
          className={cn(input, 'font-medium tracking-wide')}
        />
        <Input
          value={draft.name}
          onChange={e => setDraft(d => ({ ...d, name: e.target.value }))}
          placeholder={isNew ? 'Activity, e.g. Equipment maintenance' : ''}
          aria-label='Activity'
          readOnly={!canManage}
          className={input}
        />
        <div className='relative'>
          <Input
            type='number'
            min={0}
            max={24}
            step={0.5}
            value={draft.hours}
            onChange={e => setDraft(d => ({ ...d, hours: e.target.value }))}
            placeholder={isNew ? '1' : ''}
            aria-label='Default hours per day'
            readOnly={!canManage}
            className={cn(input, 'pr-11 text-right')}
          />
          <span className='pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground'>
            h/day
          </span>
        </div>
        <ActiveCell
          active={row?.active}
          label={row?.code}
          disabled={!canManage || toggle.isPending}
          onChange={on => toggle.mutate(on)}
        />
        <RowActions
          isNew={isNew}
          canManage={canManage}
          dirty={dirty}
          canSave={valid}
          saving={save.isPending}
          removing={remove.isPending}
          label={row?.code ?? ''}
          onSave={() => save.mutate(draft)}
          onUndo={undo}
          onRemove={() => {
            if (row && window.confirm(`Delete support activity ${row.code}?`)) remove.mutate()
          }}
        />
      </div>
    </div>
  )
}

// ── Unavailability types ─────────────────────────────────────────────────────

const UA_COLS = 'grid grid-cols-[5.5rem_minmax(0,1fr)_8.5rem_3.25rem_7rem] items-center gap-1.5'

function UnavailabilityList({ canManage, onError }: { canManage: boolean; onError: OnError }) {
  const rows = useUnavailabilityTypes()
  return (
    <ListFrame
      hint='Vacation, sick leave, holidays and other time away. Each type takes a full day, or a number of hours where you allow it.'
      cols={UA_COLS}
      headers={['Code', 'Type', 'Can take', 'Active', '']}
      loading={rows.isLoading}
      empty={rows.data?.length === 0}
      addRow={canManage && <UnavailabilityRow key='new' canManage onError={onError} />}
    >
      {rows.data?.map(r => (
        <UnavailabilityRow key={r.id} row={r} canManage={canManage} onError={onError} />
      ))}
    </ListFrame>
  )
}

function UnavailabilityRow({
  row,
  canManage,
  onError,
}: {
  row?: UnavailabilityType
  canManage: boolean
  onError: OnError
}) {
  const invalidate = useInvalidateResourceCenter()
  const { draft, setDraft, dirty, markSaved, undo } = useRowDraft({
    code: row?.code ?? '',
    name: row?.name ?? '',
    allowPartial: row?.allowPartial ?? true,
  })

  const isNew = !row
  const valid = !!draft.code.trim() && !!draft.name.trim()
  const done = () => {
    onError(null)
    void invalidate()
  }
  const fail = (e: unknown) => onError(messageOf(e))

  const save = useMutation({
    mutationFn: (sent: typeof draft) => {
      const body = { ...sent, code: sent.code.trim(), name: sent.name.trim() }
      return row
        ? resourceCenterApi.updateUnavailabilityType(row.id, body)
        : resourceCenterApi.createUnavailabilityType(body)
    },
    onSuccess: (_, sent) => {
      const blank = { code: '', name: '', allowPartial: true }
      if (isNew) setDraft(d => (JSON.stringify(d) === JSON.stringify(sent) ? blank : d))
      else markSaved(sent)
      done()
    },
    onError: fail,
  })
  const toggle = useMutation({
    mutationFn: (active: boolean) => resourceCenterApi.updateUnavailabilityType(row!.id, { active }),
    onSuccess: done,
    onError: fail,
  })
  const remove = useMutation({
    mutationFn: () => resourceCenterApi.removeUnavailabilityType(row!.id),
    onSuccess: done,
    onError: fail,
  })

  const input = isNew ? FORM_INPUT : QUIET_INPUT
  return (
    <div className={isNew ? ADD_ROW : undefined}>
      <div className={cn(UA_COLS, !isNew && ROW, row && !row.active && 'text-muted-foreground')}>
        <Input
          value={draft.code}
          onChange={e => setDraft(d => ({ ...d, code: e.target.value.toUpperCase() }))}
          placeholder={isNew ? 'Code' : ''}
          aria-label='Code'
          maxLength={12}
          readOnly={!canManage}
          className={cn(input, 'font-medium tracking-wide')}
        />
        <Input
          value={draft.name}
          onChange={e => setDraft(d => ({ ...d, name: e.target.value }))}
          placeholder={isNew ? 'Type, e.g. Vacation' : ''}
          aria-label='Type'
          readOnly={!canManage}
          className={input}
        />
        <div className='flex rounded-md bg-muted p-0.5 text-xs' role='radiogroup' aria-label='Can take'>
          {[
            { value: false, label: 'Full day' },
            { value: true, label: 'Or hours' },
          ].map(o => (
            <button
              key={String(o.value)}
              type='button'
              role='radio'
              aria-checked={draft.allowPartial === o.value}
              disabled={!canManage}
              onClick={() => setDraft(d => ({ ...d, allowPartial: o.value }))}
              className={cn(
                'flex-1 rounded px-1.5 py-1 font-medium transition-colors',
                draft.allowPartial === o.value
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              {o.label}
            </button>
          ))}
        </div>
        <ActiveCell
          active={row?.active}
          label={row?.code}
          disabled={!canManage || toggle.isPending}
          onChange={on => toggle.mutate(on)}
        />
        <RowActions
          isNew={isNew}
          canManage={canManage}
          dirty={dirty}
          canSave={valid}
          saving={save.isPending}
          removing={remove.isPending}
          label={row?.code ?? ''}
          onSave={() => save.mutate(draft)}
          onUndo={undo}
          onRemove={() => {
            if (row && window.confirm(`Delete unavailability type ${row.code}?`)) remove.mutate()
          }}
        />
      </div>
    </div>
  )
}

function ActiveCell({
  active,
  label,
  disabled,
  onChange,
}: {
  active?: boolean
  label?: string
  disabled: boolean
  onChange: (active: boolean) => void
}) {
  return (
    <span className='px-2'>
      {active !== undefined && (
        <Switch
          checked={active}
          disabled={disabled}
          onCheckedChange={onChange}
          aria-label={active ? `Deactivate ${label}` : `Reactivate ${label}`}
        />
      )}
    </span>
  )
}

/** Add on the new row; Undo + Save while a row is edited; a muted Delete otherwise. */
function RowActions({
  isNew,
  canManage,
  dirty,
  canSave,
  saving,
  removing,
  label,
  onSave,
  onUndo,
  onRemove,
}: {
  isNew: boolean
  canManage: boolean
  dirty: boolean
  canSave: boolean
  saving: boolean
  removing: boolean
  label: string
  onSave: () => void
  onUndo: () => void
  onRemove: () => void
}) {
  if (!canManage) return <span />
  if (isNew) {
    return (
      <span className='flex justify-end pr-1'>
        <Button type='button' size='sm' className='h-8' disabled={!dirty || !canSave || saving} onClick={onSave}>
          {saving ? <Loader2 className='h-3.5 w-3.5 animate-spin' /> : <Plus className='mr-1 h-3.5 w-3.5' />}
          Add
        </Button>
      </span>
    )
  }
  return (
    <span className='flex justify-end gap-1 pr-1'>
      {dirty ? (
        <>
          <Button type='button' variant='ghost' size='sm' className='h-7 px-2 text-xs' onClick={onUndo}>
            Undo
          </Button>
          <Button type='button' size='sm' className='h-7 px-2.5 text-xs' disabled={!canSave || saving} onClick={onSave}>
            {saving ? <Loader2 className='h-3.5 w-3.5 animate-spin' /> : 'Save'}
          </Button>
        </>
      ) : (
        <Button
          type='button'
          variant='ghost'
          className={DELETE_BUTTON}
          disabled={removing}
          onClick={onRemove}
          aria-label={`Delete ${label}`}
        >
          <Trash2 className='h-3.5 w-3.5' />
        </Button>
      )}
    </span>
  )
}
